import React from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import { LanguageProvider } from "./context/LanguageContext";
import { useNotificationPoller } from "./hooks/useNotificationPoller";
import { useDarkMode } from "./hooks/useDarkMode";
import { useAuth } from "./context/AuthContext";
import { isTokenExpired } from "./utils/tokenManagement";
import { setAuthFailureCallback } from "./apis/axiosConfig";
import Home from "./pages/home/Home";
import Contact from "./pages/contact/Contact";
import Footer from "./components/Footer";
import Header from "./components/Header";
import ScrollToTop from "./pages/TopToBack/ScrollToTop";
import Top from "./pages/TopToBack/Top";
// import Login from "./pages/user/Login";
// import Regsiter from "./pages/user/Regsiter";
import Signin from "./components/Signin.jsx";
import Signup from "./components/Signup.jsx";
import About from "./pages/about/About";
import ActivitiesSection from "./pages/gallery/gallery";
import Faqs from "./pages/faq/Faqs.jsx";
import OtpVerification from "./components/OtpVerification";
import Dashboard from "./components/Dashboard.jsx";
import ProtectedRoute from "./components/ProtectedRoute.jsx";

// Dashboard subpages
import ProfilePage from "./pages/dashboard/ProfilePage.jsx";
import AccountInfoPage from "./pages/dashboard/AccountInfoPage.jsx";
import CurrentPlan from "./pages/dashboard/AccountInfoPages/CurrentPlan";
import RenewableDate from "./pages/dashboard/AccountInfoPages/RenewableDate";
import PaymentMethods from "./pages/dashboard/AccountInfoPages/PaymentMethods";
import BillingHistory from "./pages/dashboard/AccountInfoPages/BillingHistory";
import LinkedDomain from "./pages/dashboard/AccountInfoPages/LinkedDomain";
import ProjectStatus from "./pages/dashboard/AccountInfoPages/ProjectStatus";
import IntegrationsPage from "./pages/dashboard/IntegrationsPage.jsx";
import PreferencePage from "./pages/dashboard/PreferencePage.jsx";
import PricingPage from "./pages/dashboard/PricingPage.jsx";
import SettingsPage from "./pages/dashboard/SettingsPage.jsx";
import ReportsData from "./pages/dashboard/ReportsData.jsx";

// User Dashboard
// import UserDashboard from "./pages/user/UserDashboard";
// import MyProfile from "./pages/user/Myrpofile";
// import MyCourse from "./pages/user/MyCourse";
// import MyJob from "./pages/user/MyJob";
import EditProfile from "./pages/dashboard/EditProfile.jsx";
import LanguagePage from "./pages/dashboard/LanguagePage.jsx";
import Notification from "./pages/dashboard/Notification.jsx";
import Need from "./pages/dashboard/Need.jsx";
import ForgetPassword from "./components/ForgetPassword.jsx";
import UpdatePassword from "./components/UpdatePassword.jsx";
import Login2FA from "./components/Login2FA.jsx";
import SeoResult from "./pages/dashboard/SeoResult.jsx";
import AcceptInvite from "./components/AcceptInvite.jsx";
import PrivacyPolicy from "./pages/dashboard/AccountInfoPages/PrivacyPolicy.jsx";
import TermsConditions from "./pages/dashboard/AccountInfoPages/TermsConditions.jsx";
import EndpointScanner from "./pages/dashboard/EndpointScanner.jsx";
import SeoEndpoints from "./pages/dashboard/SeoEndpoints.jsx";
import KeywordRankingPage from "./pages/dashboard/KeywordRankingPage.jsx";
import SeoAnalyzerPage from "./pages/dashboard/SeoAnalyzerPage.jsx";
import SeoResultsPage from "./pages/dashboard/SeoResultsPage.jsx";
import SeoHistory from "./pages/dashboard/SeoHistory.jsx";
import DomainEndpointDetailsPage from "./pages/dashboard/DomainEndpointDetailsPage.jsx";
import SeoEndpointDetailPage from "./pages/dashboard/SeoEndpointDetailPage.jsx";
import PageLevelResultsPage from "./pages/dashboard/PageLevelResultsPage.jsx";
import PageEndpointDetailsPage from "./pages/dashboard/PageEndpointDetailsPage.jsx";
import PluginConnectionPage from "./pages/dashboard/PluginConnectionPage.jsx";
import PasswordResetFlow from "./components/PasswordResetFlow.jsx";
import CursorFollower from "./components/CursorFollower.jsx";
import BlogGeneratorPage from "./pages/dashboard/BlogGeneratorPage.jsx";

const App = () => {
  const location = useLocation();
  const { isDarkMode } = useDarkMode();
  const { handleAuthFailure } = useAuth();

  // Proactively drop stale tokens so first-visit requests don't trigger noisy session toasts
  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    if (isTokenExpired(token)) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('account');
      localStorage.removeItem('userName');
      localStorage.removeItem('userEmail');
    }
  }, []);

  // Set up the auth failure callback for axios interceptor
  React.useEffect(() => {
    setAuthFailureCallback(handleAuthFailure);
  }, [handleAuthFailure]);

  useNotificationPoller(); // Start global notification polling

  const ToastCloseButton = ({ closeToast }) => (
    <button
      onClick={closeToast}
      aria-label="Close notification"
      className={`ml-3 inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors duration-150 border ${isDarkMode
        ? 'border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white'
        : 'border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-800'
        }`}
    >
      ✕
    </button>
  );

  // Hide Header/Footer on dashboard and its subpages
  const hideLayoutRoutes = ["/dashboard"];
  const shouldHideLayout = hideLayoutRoutes.some((path) =>
    location.pathname.startsWith(path)
  );

  return (
    <LanguageProvider>
      <>
        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss={false}
          draggable={true}
          pauseOnHover={false}
          limit={3}
          closeButton={(props) => <ToastCloseButton {...props} />}
          theme={isDarkMode ? 'dark' : 'light'}
        />
        {!shouldHideLayout && <Header />}
        <ScrollToTop />
        <CursorFollower />

        <Routes>
          {/* General Pages */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/gallery" element={<ActivitiesSection />} />
          <Route path="/faqs" element={<Faqs />} />

          {/* footer  */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />


          {/* Auth Pages */}
          <Route path="/signin" element={<Signin />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forget-password" element={<ForgetPassword />} />
          <Route path="/forgetpassword" element={<ForgetPassword />} />
          <Route path="/reset-password" element={<PasswordResetFlow />} />
          <Route path="/resetpassword" element={<UpdatePassword />} />
          <Route path="/update-password" element={<UpdatePassword />} />
          <Route path="/otpverify" element={<OtpVerification />} />
          <Route path="/login-2fa" element={<Login2FA />} />
          <Route path="/accept-invite/:token" element={<AcceptInvite />} />

          {/* Protected Dashboard with nested routes */}
          <Route path="/dashboard" element={<Dashboard />}>
            <Route index element={<ProfilePage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="account-info" element={<AccountInfoPage />} />
            <Route path="account-info/current-plan" element={<CurrentPlan />} />
            <Route path="account-info/renewal-date" element={<RenewableDate />} />
            <Route path="account-info/payment-methods" element={<PaymentMethods />} />
            <Route path="account-info/billing-history" element={<BillingHistory />} />
            <Route path="account-info/linked-domain" element={<LinkedDomain />} />
            <Route path="account-info/project-status" element={<ProjectStatus />} />
            <Route path="account-info/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="account-info/terms-conditions" element={<TermsConditions />} />
            <Route path="reports-data" element={<ReportsData />} />
            <Route path="integrations" element={<IntegrationsPage />} />
            <Route path="preference" element={<PreferencePage />} />
            <Route path="pricing" element={<PricingPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="edit-profile" element={<EditProfile />} />
            <Route path="language" element={<LanguagePage />} />
            <Route path="notifications" element={<Notification />} />
            <Route path="need" element={<Need />} />
            <Route path="endpoint-scanner" element={<EndpointScanner />} />
            <Route path="seo-endpoints" element={<SeoEndpoints />} />
            <Route path="keyword-ranking" element={<KeywordRankingPage />} />
            <Route path="seo-analyzer" element={<SeoAnalyzerPage />} />
            <Route path="seo-history" element={<SeoHistory />} />
            <Route path="projects/:id/seo-results" element={<SeoResultsPage />} />
            <Route path="seo-results/:projectId" element={<SeoResultsPage />} />
            <Route path="seo-endpoint-detail/:projectId/:endpointName" element={<SeoEndpointDetailPage />} />
            <Route path="seo-endpoint-details/:projectId/:endpointId" element={<DomainEndpointDetailsPage />} />
            <Route path="seo-page-results/:projectId" element={<PageLevelResultsPage />} />
            <Route path="seo-page-results/:projectId/endpoints/:endpointId" element={<PageEndpointDetailsPage />} />
            <Route path="plugin-connection" element={<PluginConnectionPage />} />
            <Route path="seo-result" element={<SeoResult />} />
            <Route path="blog-generator/:id" element={<BlogGeneratorPage />} />
          </Route>
          {/* User Dashboard */}
          {/* <Route path="/user" element={<UserDashboard />}>
          <Route path="myprofile" element={<MyProfile />} />
          <Route path="myjobs" element={<MyJob />} />
          <Route path="mycourses" element={<MyCourse />} />
        </Route> */}
        </Routes>

        {!shouldHideLayout && <Top />}
        {!shouldHideLayout && <Footer />}
      </>
    </LanguageProvider>
  );
};

export default App;